import { Menu, Text, useTheme } from '@ltvco/refresh-lib/theme';
import {
  ExpandLess,
  ExpandMore,
  Home,
  TuneRounded,
  Work,
} from '@mui/icons-material';
import { KeyboardEvent, useState } from 'react';

import {
  IntroTooltipBody,
  IntroTooltipCta,
  IntroTooltipRoot,
  IntroTooltipTitle,
  ViewSelectButton,
  ViewSelectButtonLabel,
  ViewSelectCluster,
  ViewSelectMenuHeader,
  ViewSelectMenuItem,
  ViewSelectMenuItemCopy,
  ViewSelectMenuItemDescription,
  ViewSelectMenuItemIcon,
  ViewSelectMenuItemTitle,
  ViewSelectRow,
} from './PropertyReportViewSelect.styles';

import type { PropertyReportSectionView } from 'hooks/usePropertyReportSectionOrder';

const VIEW_OPTIONS = [
  {
    value: 'default' as const,
    label: 'Homebuyer',
    description: 'See value, potential ownership and taxes first.',
    Icon: Home,
  },
  {
    value: 'professional' as const,
    label: 'Real Estate Professional',
    description: 'See rent, equity, market-value, deeds, and loans first',
    Icon: Work,
  },
];

export type PropertyReportViewSelectProps = {
  view: PropertyReportSectionView;
  onChange: (view: PropertyReportSectionView) => void;
  showIntro?: boolean;
  onDismissIntro?: () => void;
};

export function PropertyReportViewSelect({
  view,
  onChange,
  showIntro = false,
  onDismissIntro,
}: PropertyReportViewSelectProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const menuId = 'property-report-view-select-menu';
  const selectedLabel =
    VIEW_OPTIONS.find((option) => option.value === view)?.label ?? 'Homebuyer';

  const handleOpen = (target: HTMLElement) => {
    setAnchorEl(target);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (nextView: PropertyReportSectionView) => {
    onChange(nextView);
    handleClose();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpen(event.currentTarget);
    }
  };

  return (
    <ViewSelectRow id="property-report-view-select">
      <ViewSelectCluster>
        {showIntro && (
          <IntroTooltipRoot
            id="report-views-intro-tooltip"
            role="status"
            data-cy="report-views-intro-tooltip"
          >
            <IntroTooltipTitle variant="h3">
              Introducing New Report Views
            </IntroTooltipTitle>
            <IntroTooltipBody variant="body2">
              You can now see your property reports in the view of a “Homebuyer”
              or “Real-Estate Professional”.
            </IntroTooltipBody>
            <IntroTooltipCta
              variant="contained"
              color="primary"
              onClick={onDismissIntro}
              data-cy="report-views-intro-cta"
            >
              Got it!
            </IntroTooltipCta>
          </IntroTooltipRoot>
        )}
        <ViewSelectButton
          onClick={(event) => handleOpen(event.currentTarget)}
          onKeyDown={handleKeyDown}
          role="button"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-controls={isOpen ? menuId : undefined}
          aria-label="View property report as"
          tabIndex={0}
          data-cy="property-report-view-select"
        >
          <ViewSelectButtonLabel>
            <TuneRounded
              aria-hidden
              sx={{ color: theme.palette.primary.main, fontSize: 20 }}
            />
            <Text variant="body2" component="span" mb={0}>
              View as:{' '}
              <Text variant="body2" component="span" fontWeight="bold" mb={0}>
                {selectedLabel}
              </Text>
            </Text>
          </ViewSelectButtonLabel>
          {isOpen ? (
            <ExpandLess
              sx={{ fontSize: 24, color: theme.palette.text.primary }}
            />
          ) : (
            <ExpandMore
              sx={{ fontSize: 24, color: theme.palette.text.primary }}
            />
          )}
        </ViewSelectButton>
      </ViewSelectCluster>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ disablePadding: true }}
        slotProps={{
          paper: {
            sx: {
              width: 336,
              maxWidth: 'calc(100vw - 32px)',
              boxSizing: 'border-box',
              padding: 2,
              marginTop: 1,
              borderRadius: '20px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.12)',
            },
          },
        }}
      >
        <ViewSelectMenuHeader role="presentation">
          View this report as:
        </ViewSelectMenuHeader>
        {VIEW_OPTIONS.map(({ value, label, description, Icon }) => (
          <ViewSelectMenuItem
            key={value}
            selected={value === view}
            onClick={() => handleSelect(value)}
          >
            <ViewSelectMenuItemIcon aria-hidden>
              <Icon />
            </ViewSelectMenuItemIcon>
            <ViewSelectMenuItemCopy>
              <ViewSelectMenuItemTitle>{label}</ViewSelectMenuItemTitle>
              <ViewSelectMenuItemDescription>
                {description}
              </ViewSelectMenuItemDescription>
            </ViewSelectMenuItemCopy>
          </ViewSelectMenuItem>
        ))}
      </Menu>
    </ViewSelectRow>
  );
}
